/**
 * Quick-task draft handoff store (Spec 195).
 *
 * One-shot draft transfer from the QuickCreateTask sheet to the full
 * CreateTaskV2 page. sessionStorage is intentional: per-tab, never persisted
 * across browser restart, never sent over the wire (unlike URL params).
 *
 * Per-user keying prevents drafts leaking between accounts on shared devices.
 */

const KEY_PREFIX = 'helm.quickTaskDraft.';

export interface QuickTaskDraft {
  title: string;
  details: string;
  priority: 'low' | 'medium' | 'high';
}

function storageKey(userId: string): string {
  return `${KEY_PREFIX}${userId}`;
}

function getStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export const quickTaskDraftStore = {
  set(userId: string, draft: QuickTaskDraft): void {
    const storage = getStorage();
    if (!storage || !userId) return;
    try {
      storage.setItem(storageKey(userId), JSON.stringify(draft));
    } catch {
      // sessionStorage can throw in private mode or when full — silent fail is fine
      // since the handoff is a UX nicety, not a correctness requirement.
    }
  },

  consume(userId: string): QuickTaskDraft | null {
    const storage = getStorage();
    if (!storage || !userId) return null;
    const key = storageKey(userId);
    const raw = storage.getItem(key);
    if (!raw) return null;
    storage.removeItem(key);
    try {
      const parsed = JSON.parse(raw) as Partial<QuickTaskDraft>;
      if (typeof parsed.title !== 'string') return null;
      const priority = parsed.priority;
      if (priority !== 'low' && priority !== 'medium' && priority !== 'high') return null;
      return {
        title: parsed.title,
        details: typeof parsed.details === 'string' ? parsed.details : '',
        priority,
      };
    } catch {
      return null;
    }
  },

  clear(userId: string): void {
    const storage = getStorage();
    if (!storage || !userId) return;
    storage.removeItem(storageKey(userId));
  },
};
